import classNames from 'classnames';
import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FiBell, FiEdit, FiEye } from 'react-icons/fi';
import { MdArchive, MdUnarchive, MdDelete } from 'react-icons/md';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { push } from 'react-router-redux';
import {
  lifecycle,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators, compose } from 'redux';
import {
  dateFormats,
  routes,
  TaskCompletionStatus
} from '../../../../../constants';
import {
  forbidPropagate,
  fullName,
  getCpdHours,
  getDueDateText
} from '../../../../../services';
import { businessSelector } from '../../../../../state/modules/business';
import { taskActions } from '../../../../../state/modules/task';
import { pluralise } from '../../../../../utils';
import { Icon, SortableTable } from '../../../../components';
import ConfirmationModal from '../../../../components/confirmation-modal';
import Loading from '../../../loading';

/**
 * Structural table column definitions detailing sorting configurations,
 * alignment class parameters, and display names.
 */
const columns = [
  {
    name: 'title',
    display: () => 'Task Name',
    className: () => 'title',
    sortable: true
  },
  {
    name: 'user',
    display: () => 'Assigned to',
    className: () => 'user'
  },
  {
    name: 'status',
    display: () => 'Status',
    className: () => 'status'
  },
  {
    name: 'dueDate',
    display: () => 'Due Date',
    className: () => 'dueDate',
    sortable: true
  },
  {
    name: 'completionDate',
    display: () => 'Completion Date',
    className: () => 'completionDate'
  },
  {
    name: 'assigner',
    display: () => 'Assigned by',
    className: () => 'assigner'
  },
  {
    name: 'operations',
    display: () => 'Actions',
    className: () => 'operations'
  }
];

/**
 * Renders a sortable data layout displaying current tasks set for team members,
 * supporting row status evaluation, interactive loadings, or empty fallback structures.
 */
const TeamTasksTable = ({ rows, expandedParentId, sortBy, isFetching }) => (
  <div>
    {rows.length > 0 ? (
      <SortableTable
        className='table--tasks table--team-tasks'
        isFetching={false}
        unclickable={false}
        sortBy={sortBy}
        rows={rows}
        columns={columns}
      />
    ) : isFetching ? (
      <Row className='no-gutter top-gap'>
        <Col xs={12} md={6} mdOffset={3} className='boxed'>
          <Loading />
        </Col>
      </Row>
    ) : (
      <Row className='no-gutter top-gap'>
        <Col xs={12} md={6} mdOffset={3} className='boxed'>
          <div className='complete task--empty-data'>
            <Icon icon='caddie-the-dog' />
            <h1>No assigned tasks</h1>
            <p className='no-margin'>
              You haven't set any tasks for your team members yet.
            </p>
            <p className='no-margin'>
              To assign a task, use Create New Task above
            </p>
          </div>
        </Col>
      </Row>
    )}
  </div>
);

/** Resolves and HTML-decodes an individual user's full name directly from task.members */
const getUser = ({ userId }, taskMembers = []) => {
  const userObj = fp.find(({ id }) => id === userId)(taskMembers);
  return <span>{he.decode(fullName(userObj))}</span>;
};

/**
 * Resolves assignment layout string elements. Handles singular name presentation
 * or count indicators for multi-user assignment blocks.
 */
const getUsers = (users, taskMembers, toggleParent) => {
  return users.length > 1 ? (
    <div id='multiple-user-dropdown' onClick={forbidPropagate}>
      <Button className='no-padding' bsStyle='plain' onClick={toggleParent}>
        {`${users.length} ${pluralise('member', users.length)}`}
      </Button>
    </div>
  ) : (
    getUser(fp.first(users), taskMembers)
  );
};

/** Determines the global visual badge styling label to present based on underlying user assignment state metrics. */
const getTaskStatus = (users, isArchived) => {
  if (isArchived === true) {
    return <span className='label-archived'>Archived</span>;
  }

  const completed = fp.every(
    ({ status }) => status === TaskCompletionStatus.Completed
  )(users);

  if (completed) {
    return <span className='label-completed'>Completed</span>;
  }

  const notStarted = fp.every(
    ({ status }) => status === TaskCompletionStatus.NotStarted
  )(users);
  if (notStarted) {
    return <span className='label-not-started'>Not Started</span>;
  }
  return <span className='label-in-progress'>In Progress</span>;
};

/** Evaluates completion timelines across task users to display the most recent short-date configuration string. */
const getCompletionDate = (users) => {
  const completed = fp.every(
    ({ status }) => status === TaskCompletionStatus.Completed
  )(users);
  const latestCompletionDate = fp.flow(
    fp.filter(({ completionDate }) => completionDate),
    fp.map(({ completionDate }) =>
      dateFnsCompat.parse(completionDate).getTime()
    ),
    (dates) => Math.max(...dates),
    (latest) =>
      dateFnsCompat.format(dateFnsCompat.parse(latest), dateFormats.ShortDate)
  )(users);
  return <div>{completed ? latestCompletionDate : '-'}</div>;
};

// ==========================================================================
// static text templates protected from runtime context overwrites
// ==========================================================================
const REMINDER_MESSAGES = {
  JOB_RUNNING_GROUP: (
    <span>
      A reminder dispatch is already running for this task. Please allow it to
      finish naturally.
    </span>
  ),
  JOB_RUNNING_SINGLE: (
    <span>
      A reminder dispatch is already running for this user. Please allow it to
      finish naturally.
    </span>
  ),
  RECENTLY_REMINDED: (
    <span>
      A reminder has already been sent for this task within the past 24 hours,
      are you sure you want to send another reminder about this task?
    </span>
  ),
  STANDARD_GROUP: (
    <span>
      You're about to send a reminder to your team members who have not yet
      completed this task. We won't send this to anyone who has already
      completed the task you set.
    </span>
  ),
  STANDARD_SINGLE: (name) => (
    <span>
      You’re about to send a reminder to {name} to complete the task you set.
    </span>
  )
};

/**
 * Presentation modal rendering active background job status data,
 * execution progress metrics, or historical confirmation warnings during alerts updates.
 */
const ReminderModal = ({
  modalKey,
  show,
  cancel,
  onConfirm,
  isLoading,
  isJobRunning,
  activeEmailJob,
  is429Error,
  recentlyReminded,
  isGroup,
  singleUserName
}) => {
  const showErrorHighlight = is429Error && isJobRunning ? true : false;
  const showActiveJobLayout = isJobRunning;

  const headingText = showActiveJobLayout
    ? 'Reminder Dispatch in Progress'
    : 'Send Reminder';
  const cancelLabel = showActiveJobLayout
    ? 'Keep Current Dispatch Running'
    : "I'll remind later";

  const renderMessageContent = () => {
    if (showActiveJobLayout) {
      return isGroup
        ? REMINDER_MESSAGES.JOB_RUNNING_GROUP
        : REMINDER_MESSAGES.JOB_RUNNING_SINGLE;
    }
    if (recentlyReminded) {
      return REMINDER_MESSAGES.RECENTLY_REMINDED;
    }
    return isGroup
      ? REMINDER_MESSAGES.STANDARD_GROUP
      : REMINDER_MESSAGES.STANDARD_SINGLE(singleUserName);
  };

  return (
    <ConfirmationModal
      key={modalKey}
      headingText={<span>{headingText}</span>}
      cancelLabel={cancelLabel}
      actionLabel='Send Reminder'
      show={show}
      isLoading={isLoading}
      cancel={cancel}
      actions={[
        {
          onConfirmAction: onConfirm,
          actionLabel: showActiveJobLayout
            ? 'Start New Reminder'
            : 'Send Reminder'
        }
      ]}
      message={
        <div className='sync-modal-wrapper'>
          <div
            className={classNames(
              'top-gap-xs bottom-gap-sm sync-modal-message-text',
              {
                'popout-error-text': showErrorHighlight
              }
            )}
            key={
              showErrorHighlight
                ? `err-${modalKey}-${isLoading}`
                : `norm-${modalKey}`
            }
          >
            {renderMessageContent()}
          </div>

          {showActiveJobLayout && activeEmailJob && (
            <React.Fragment>
              <strong className='sync-modal-subheading'>
                Current Dispatch Details:
              </strong>
              <div className='top-gap-xs shift-up-xs sync-modal-details-box'>
                <ul className='no-margin sync-modal-metrics-list'>
                  <li>
                    <strong>Status:</strong>{' '}
                    <span className='sync-modal-status-highlight'>
                      {activeEmailJob.status || 'PROCESSING'}
                    </span>
                  </li>
                  <li>
                    <strong>Progress:</strong> {activeEmailJob.percentage}%
                    completed
                  </li>
                  <li>
                    <strong>Notifications Sent:</strong>{' '}
                    {activeEmailJob.processedCount} of{' '}
                    {activeEmailJob.totalCount} items
                  </li>
                </ul>
              </div>
              <div className='sync-modal-footer-text'>
                Starting a new reminder dispatch will cancel the current one. It
                is recommended to only use <strong>START NEW REMINDER</strong>{' '}
                if the progress bar freezes completely and fails to move for
                more than 2 minutes.
              </div>
            </React.Fragment>
          )}
        </div>
      }
    />
  );
};

/**
 * Factory transformer structuring baseline task records into configured layout blocks,
 * matching interactive tracking operations and relational sub-rows for the child views layer.
 */
const formatRow =
  ({
    archiveModalData,
    setTaskArchive,
    closeSetArchiveModal,
    data,
    deleteTask,
    deleteTaskUser,
    editable,
    goTo,
    isSendingReminder,
    openSetArchiveModal,
    sendTaskReminder,
    showDeleteModal,
    showDeleteUserModal,
    showRemindTaskModal,
    showRemindUserModal,
    toggleDeleteModal,
    toggleDeleteUserModal,
    toggleExpandedParent,
    toggleRemindTaskModal,
    toggleRemindUserModal,
    removeTaskDismissal,
    businessEmailJobStatuses = [],
    is429Error,
    cleanTask,
    business
  }) =>
  (task, index) => {
    const noReminders = fp.flow(fp.flatMap('reminders'), fp.isEmpty)(data);
    const dueDate = dateFnsCompat.parse(task.dueDate);
    const { dueDateText, pastDue, payAttention } = getDueDateText(dueDate);
    const nextRoute = editable
      ? routes.app.tasks.edit(task.id)
      : routes.app.tasks.view(task.id);
    const toggleParent = () => toggleExpandedParent(task.id);

    const reminderText = fp.flow((reminders) => {
      if (reminders.length > 0) {
        return `(${reminders.length} sent)`;
      } else {
        return null;
      }
    })(task.reminders);
    const lastReminder = fp.last(task.reminders);
    const recentlyReminded = lastReminder
      ? dateFnsCompat.parse(lastReminder.date) >
        dateFnsCompat.subHours(Date.now(), 24)
      : false;
    const completed = fp.every(
      ({ status }) => status === TaskCompletionStatus.Completed
    )(task.users);

    const activeEmailJob = fp.find(
      (job) =>
        String(job.targetId) === String(task.id) &&
        (job.status === 'PENDING' || job.status === 'PROCESSING')
    )(businessEmailJobStatuses);

    const isJobRunning = !!activeEmailJob;

    const reminderOptions = completed ? (
      <span
        id='reminder-options'
        className={classNames({ completed, noReminders })}
      ></span>
    ) : (
      <span
        id='reminder-options'
        className={classNames({ completed, noReminders })}
      >
        <span
          key='send-reminder'
          className='send-reminder'
          onClick={() => {
            cleanTask();
            toggleRemindTaskModal(task.id);
          }}
        >
          <FiBell />
        </span>
        {noReminders ? null : (
          <span key='reminder-text' className='reminder-text'>
            {reminderText}
          </span>
        )}

        <ReminderModal
          modalKey={`task-reminder-${task.id}`}
          show={showRemindTaskModal === task.id}
          cancel={() => toggleRemindTaskModal(task.id)}
          isLoading={isSendingReminder}
          isJobRunning={isJobRunning}
          activeEmailJob={activeEmailJob}
          is429Error={is429Error}
          recentlyReminded={recentlyReminded}
          isGroup={task.users.length > 1}
          singleUserName={fullName(fp.first(task.members))}
          onConfirm={() => {
            cleanTask();
            // Parent Row Bell: Omit targetUserIds -> backend reminds all eligible incomplete users
            sendTaskReminder(task.id, business.id);
            removeTaskDismissal(task.id);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      </span>
    );

    return {
      id: task.id,
      className: 'parent-row',
      handler: () => goTo(nextRoute),
      title: (
        <LinkContainer onClick={forbidPropagate} to={nextRoute}>
          <span>{he.decode(task.name)}</span>
        </LinkContainer>
      ),
      dueDate: (
        <span className={pastDue ? 'past-due' : ''}>
          {payAttention ? <Icon icon='warning-large' /> : null} {dueDateText}
        </span>
      ),
      assigner: <span>{he.decode(task.assigner)}</span>,
      cpdHours: <span>{getCpdHours(task)}</span>,
      user: getUsers(task.users, task.members, toggleParent),
      status: getTaskStatus(task.users, task.isArchived),
      completionDate: getCompletionDate(task.users),
      operations: (
        <div className='flexbox' onClick={forbidPropagate}>
          {reminderOptions}
          <span
            key='edit-task'
            className={classNames('edit-task')}
            onClick={() => goTo(nextRoute)}
          >
            {editable ? <FiEdit /> : <FiEye />}
          </span>
          <span
            key={`set-archive-button-${index}`}
            className='set-archive-task'
            onClick={() => {
              openSetArchiveModal(task.id, !task.isArchived);
            }}
          >
            {task.isArchived ? (
              <MdUnarchive size={16} color='#1fd1a1' />
            ) : (
              <MdArchive size={16} color='grey' />
            )}
          </span>
          <span
            key='remove-task'
            className={classNames('remove-task', {
              'remove-task--hidden': !editable
            })}
            onClick={() => toggleDeleteModal(task.id)}
          >
            <MdDelete size={16} />
          </span>
          <ConfirmationModal
            key={`delete-${task.id}`}
            actions={[
              {
                onConfirmAction: () => deleteTask(task.id),
                actionLabel: 'Remove'
              }
            ]}
            cancel={() => toggleDeleteModal(task.id)}
            message={
              <span>
                You are about to remove <b>{he.decode(task.name)}</b>. &nbsp;
                Any CPD earned by users for this task will be tracked against
                their CPD progress in their CPD activity log.
              </span>
            }
            headingText={<span>Remove Task</span>}
            show={showDeleteModal === task.id}
          />
          <ConfirmationModal
            key={`set-archive-confirmation-modal-${task.id}`}
            actions={[
              {
                onConfirmAction: () =>
                  setTaskArchive(task.id, archiveModalData.isArchived),
                actionLabel:
                  archiveModalData && archiveModalData.isArchived
                    ? 'Archive'
                    : 'Unarchive'
              }
            ]}
            cancel={() => closeSetArchiveModal()}
            message={
              <span>
                You are about to{' '}
                {archiveModalData && archiveModalData.isArchived
                  ? 'archive'
                  : 'unarchive'}{' '}
                <b>{he.decode(task.name)}</b>.
                <br />
                {archiveModalData && archiveModalData.isArchived ? (
                  <span className='text-sm'>
                    Archived tasks will no longer be shown to assignees who
                    hadn't completed the task in <b>My Tasks</b>. Instead, they
                    will be shown in <b>My Task History</b>.
                  </span>
                ) : (
                  <span className='text-sm'>
                    Unarchived tasks will reappear in <b>My Tasks</b> for
                    assignees who hadn't already completed the task.
                  </span>
                )}
              </span>
            }
            headingText={<span>Archive Task</span>}
            show={archiveModalData && archiveModalData.taskId === task.id}
          />
        </div>
      ),
      childrenRows:
        task.users.length > 1
          ? fp.map((user) => {
              const userReminders = fp.filter(
                ({ receiver }) => receiver === user.userId
              )(task.reminders);
              const reminderText = fp.flow((reminders) => {
                if (reminders.length > 0) {
                  return `(${reminders.length} sent)`;
                } else {
                  return null;
                }
              })(userReminders);
              const lastReminder = fp.last(userReminders);
              const recentlyReminded = lastReminder
                ? dateFnsCompat.parse(lastReminder.date) >
                  dateFnsCompat.subHours(Date.now(), 24)
                : false;
              const completed = user.status === TaskCompletionStatus.Completed;

              const reminderOptions = completed ? (
                <div
                  id='reminder-options'
                  className={classNames({ completed, noReminders })}
                ></div>
              ) : (
                <div
                  id='reminder-options'
                  className={classNames({ completed, noReminders })}
                >
                  <span
                    key='send-reminder'
                    className='send-reminder'
                    onClick={() => {
                      cleanTask();
                      toggleRemindUserModal(user.userId);
                    }}
                  >
                    <FiBell />
                  </span>
                  {noReminders ? null : (
                    <span key='reminder-text' className='reminder-text'>
                      {reminderText}
                    </span>
                  )}

                  <ReminderModal
                    modalKey={`user-reminder-${user.userId}`}
                    show={showRemindUserModal === user.userId}
                    cancel={() => toggleRemindUserModal(user.userId)}
                    isLoading={isSendingReminder}
                    isJobRunning={isJobRunning}
                    activeEmailJob={activeEmailJob}
                    is429Error={is429Error}
                    recentlyReminded={recentlyReminded}
                    isGroup={false}
                    singleUserName={fullName(
                      fp.find(({ id }) => id === user.userId)(task.members)
                    )}
                    onConfirm={() => {
                      cleanTask();
                      // Child Row Bell: Pass [user.userId] -> backend targets this specific user only
                      sendTaskReminder(task.id, business.id, [user.userId]);
                      removeTaskDismissal(task.id);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  />
                </div>
              );

              return {
                className: 'child-row',
                user: getUser(user, task.members),
                status: getTaskStatus([user]),
                completionDate: getCompletionDate([user]),
                operations: (
                  <div className='flexbox'>
                    {reminderOptions}
                    <span
                      key='remove-task-user'
                      className={classNames('remove-task-user')}
                      onClick={() => toggleDeleteUserModal(user.userId)}
                    >
                      <MdDelete />
                    </span>
                    <ConfirmationModal
                      key={`delete-${user.userId}`}
                      actions={[
                        {
                          onConfirmAction: () =>
                            deleteTaskUser(task.id, user.userId),
                          actionLabel: 'Remove'
                        }
                      ]}
                      cancel={() => toggleDeleteUserModal(user.userId)}
                      message={
                        <span>
                          You are about to remove{' '}
                          <b>{getUser(user, task.members)}</b> from{' '}
                          <b>{he.decode(task.name)}</b>. &nbsp; Any CPD earned
                          by users for this task will be tracked against their
                          CPD progress in their CPD activity log.
                        </span>
                      }
                      headingText={<span>Remove User From Task</span>}
                      show={showDeleteUserModal === user.userId}
                    />
                  </div>
                )
              };
            })(task.users)
          : []
    };
  };

const mapStateToProps = (state) => ({
  allCpdCategorys: state.cpdCategorys.cpdCategorys,
  business: businessSelector(state),
  industryCpdCategory: state.industry.cpdCategorys,
  isSendingReminder: state.task.isSendingReminder,
  businessEmailJobStatuses: state.task.businessEmailJobStatuses || [],
  is429Error: state.task.is429Error
});

const mapDispatchToProps = (dispatch) => ({
  deleteTask: bindActionCreators(taskActions.task.deleteTask, dispatch),
  deleteTaskUser: bindActionCreators(taskActions.task.deleteTaskUser, dispatch),
  getBusinessTasks: bindActionCreators(
    taskActions.task.getBusinessTasks,
    dispatch
  ),
  goTo: bindActionCreators(push, dispatch),
  patchTask: bindActionCreators(taskActions.task.patchTask, dispatch),
  sendTaskReminder: bindActionCreators(taskActions.task.sendReminder, dispatch),
  cleanTask: bindActionCreators(taskActions.task.cleanTask, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    {
      archiveModalData: undefined,
      expandedParentId: [],
      showDeleteModal: undefined,
      showDeleteUserModal: undefined,
      showRemindTaskModal: undefined,
      showRemindUserModal: undefined
    },
    {
      closeSetArchiveModal: () => () => ({
        archiveModalData: undefined
      }),
      openSetArchiveModal: () => (taskId, isArchived) => ({
        archiveModalData: {
          taskId: taskId,
          isArchived
        }
      }),
      resetReminderModals: () => () => ({
        showRemindUserModal: undefined,
        showRemindTaskModal: undefined
      }),
      toggleDeleteModal:
        ({ showDeleteModal }) =>
        (parentId) => ({
          showDeleteModal: parentId === showDeleteModal ? undefined : parentId
        }),
      toggleDeleteUserModal:
        ({ showDeleteUserModal }) =>
        (userId) => ({
          showDeleteUserModal:
            userId === showDeleteUserModal ? undefined : userId
        }),
      toggleExpandedParent:
        ({ expandedParentId }) =>
        (parentId) => ({
          expandedParentId: expandedParentId.includes(parentId)
            ? expandedParentId.filter((id) => id !== parentId)
            : expandedParentId.concat([parentId])
        }),
      toggleRemindTaskModal:
        ({ showRemindTaskModal }) =>
        (taskId) => ({
          showRemindTaskModal:
            taskId === showRemindTaskModal ? undefined : taskId
        }),
      toggleRemindUserModal:
        ({ showRemindUserModal }) =>
        (userId) => ({
          showRemindUserModal:
            userId === showRemindUserModal ? undefined : userId
        })
    }
  ),
  withStateHandlers(
    {
      filter: {
        sort: '',
        skip: 0
      }
    },
    {
      onQuerying: () => (filter) => ({
        filter
      })
    }
  ),
  withHandlers({
    sortBy:
      ({ filter, onQuerying, callback }) =>
      (field, sortOrder) => {
        const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
        const nextFilter = { ...filter, sort: sortParam, skip: 0 };
        onQuerying(nextFilter);
        callback(nextFilter);
      }
  }),
  withProps(
    ({
      patchTask,
      callback,
      deleteTask,
      deleteTaskUser,
      closeSetArchiveModal,
      toggleDeleteModal,
      toggleDeleteUserModal,
      showDeleteModal,
      showDeleteUserModal
    }) => ({
      setTaskArchive: (taskId, isArchived) => {
        closeSetArchiveModal();
        patchTask(taskId, isArchived, callback);
      },
      deleteTask: (taskId) => {
        toggleDeleteModal(showDeleteModal);
        deleteTask(taskId, callback);
      },
      deleteTaskUser: (taskId, userId) => {
        toggleDeleteUserModal(showDeleteUserModal);
        deleteTaskUser(taskId, userId, callback);
      }
    })
  ),
  withProps((props) => ({
    rows: fp.flow(
      fp.map.convert({ cap: false })(formatRow(props)),
      fp.flatMap((row) => {
        if (props.expandedParentId.includes(row.id)) {
          return [row, ...row.childrenRows];
        } else {
          return [row];
        }
      })
    )(props.data)
  })),
  lifecycle({
    componentDidUpdate(prevProps) {
      const { isSendingReminder, is429Error, resetReminderModals } = this.props;

      const wasSending = prevProps.isSendingReminder;
      const isSendingNow = isSendingReminder;

      if (wasSending && !isSendingNow) {
        if (is429Error === false) {
          resetReminderModals();
        }
      }
    }
  })
)(TeamTasksTable);



// WEBPACK FOOTER //
// ./src/containers/pages/app/tasks/partials/team-tasks-table.js